/**
 * BidBandit Chakra UI Theme
 *
 * Modern high-tech light theme with MACC branding.
 * Colors: Red (Primary), White/Silver (Backgrounds), Black/Gray (Text)
 */

import { extendTheme, type ThemeConfig } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';

const config: ThemeConfig = {
    initialColorMode: 'light',
    useSystemColorMode: false,
};

const colors = {
    brand: {
        50: '#FFF5F5',
        100: '#FED7D7',
        200: '#FEB2B2',
        300: '#FC8181',
        400: '#F56565',
        500: '#E53E3E', // Primary MACC Red
        600: '#C53030',
        700: '#9B2C2C',
        800: '#822727',
        900: '#63171B',
    },
    // Silver/Gray scale for modern tech feel
    silver: {
        50: '#F7FAFC',
        100: '#EDF2F7',
        200: '#E2E8F0', // Classic silver
        300: '#CBD5E0',
        400: '#A0AEC0',
        500: '#718096',
        600: '#4A5568',
        700: '#2D3748',
        800: '#1A202C',
        900: '#171923',
    },
    // We keep navy for compatibility but map it to cool grays in light mode
    navy: {
        50: '#F7FAFC',
        100: '#EDF2F7',
        200: '#E2E8F0',
        300: '#CBD5E0',
        400: '#A0AEC0',
        500: '#718096',
        600: '#4A5568',
        700: '#2D3748',
        800: '#1A202C',
        900: '#171923',
    }
};

const fonts = {
    heading: `'Inter', -apple-system, BlinkMacSystemFont, sans-serif`,
    body: `'Inter', -apple-system, BlinkMacSystemFont, sans-serif`,
};

const styles = {
    global: (props: any) => ({
        'html, body': {
            bg: mode('gray.50', 'gray.900')(props),
            color: mode('gray.800', 'white')(props),
        },
        '::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
        },
        '::-webkit-scrollbar-track': {
            bg: mode('gray.100', 'gray.800')(props),
        },
        '::-webkit-scrollbar-thumb': {
            bg: mode('gray.300', 'gray.600')(props),
            borderRadius: 'full',
            _hover: {
                bg: mode('gray.400', 'gray.500')(props),
            }
        },
    }),
};

const components = {
    Card: {
        baseStyle: (props: any) => ({
            container: {
                bg: mode('white', 'gray.800')(props),
                borderRadius: 'xl',
                borderWidth: '0px', // Explicitly remove border width
                boxShadow: mode(
                    '0 4px 20px 0 rgba(0, 0, 0, 0.05)', // Premium soft shadow
                    '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                )(props),
            },
        }),
    },
    Button: {
        baseStyle: {
            fontWeight: 'semibold',
            borderRadius: 'lg',
        },
        defaultProps: {
            colorScheme: 'brand',
        },
        variants: {
            solid: (props: any) => ({
                bg: props.colorScheme === 'brand' ? 'brand.500' : `${props.colorScheme}.500`,
                color: 'white',
                _hover: {
                    bg: props.colorScheme === 'brand' ? 'brand.600' : `${props.colorScheme}.600`,
                    boxShadow: 'md',
                },
                _active: {
                    bg: props.colorScheme === 'brand' ? 'brand.700' : `${props.colorScheme}.700`,
                }
            }),
            ghost: (props: any) => ({
                color: mode('gray.600', 'gray.300')(props),
                _hover: {
                    bg: mode('gray.100', 'gray.700')(props),
                    color: mode('brand.600', 'white')(props),
                },
            }),
            outline: (props: any) => ({
                borderColor: mode('brand.500', 'brand.400')(props),
                color: mode('brand.600', 'brand.300')(props),
                _hover: {
                    bg: mode('brand.50', 'whiteAlpha.100')(props),
                },
            }),
        },
    },
    Input: {
        variants: {
            filled: (props: any) => ({
                field: {
                    bg: mode('white', 'gray.800')(props),
                    border: '1px solid',
                    borderColor: mode('gray.200', 'gray.600')(props),
                    _hover: {
                        bg: mode('gray.50', 'gray.700')(props),
                        borderColor: mode('gray.300', 'gray.500')(props),
                    },
                    _focus: {
                        bg: mode('white', 'gray.800')(props),
                        borderColor: 'brand.500',
                        boxShadow: `0 0 0 1px ${colors.brand[500]}`,
                    },
                },
            }),
        },
        defaultProps: {
            variant: 'filled',
        },
    },
    Table: {
        variants: {
            simple: (props: any) => ({
                th: {
                    borderColor: mode('gray.200', 'gray.700')(props),
                    color: mode('gray.500', 'gray.400')(props),
                    textTransform: 'uppercase',
                    fontSize: 'xs',
                    letterSpacing: 'wider',
                    fontWeight: 'bold',
                },
                td: {
                    borderColor: mode('gray.100', 'gray.700')(props),
                    color: mode('gray.800', 'gray.200')(props),
                },
                tbody: {
                    tr: {
                        _hover: {
                            bg: mode('gray.50', 'gray.700')(props),
                        },
                    },
                },
            }),
        },
    },
    Modal: {
        baseStyle: (props: any) => ({
            dialog: {
                bg: mode('white', 'gray.800')(props),
                borderRadius: 'xl',
                boxShadow: 'xl',
            },
            header: {
                color: mode('gray.800', 'white')(props),
            },
            closeButton: {
                color: mode('gray.500', 'gray.400')(props),
            }
        }),
    },
    Menu: {
        baseStyle: (props: any) => ({
            list: {
                bg: mode('white', 'gray.800')(props),
                borderColor: mode('gray.200', 'gray.600')(props),
                boxShadow: 'lg',
                borderRadius: 'lg',
                borderWidth: '1px',
            },
            item: {
                bg: mode('white', 'gray.800')(props),
                color: mode('gray.700', 'gray.200')(props),
                _hover: {
                    bg: mode('gray.100', 'gray.700')(props),
                    color: mode('brand.600', 'white')(props),
                },
                _focus: {
                    bg: mode('gray.100', 'gray.700')(props),
                },
            },
        }),
    },
    Stat: {
        baseStyle: (props: any) => ({
            label: {
                color: mode('gray.500', 'gray.400')(props),
            },
            number: {
                color: mode('gray.800', 'white')(props),
            },
            helpText: {
                color: mode('gray.500', 'gray.400')(props),
            },
        }),
    },
    Heading: {
        baseStyle: (props: any) => ({
            color: mode('gray.800', 'white')(props),
        }),
    },
    Text: {
        baseStyle: (props: any) => ({
            color: mode('gray.600', 'gray.300')(props),
        }),
    },
};

export const theme = extendTheme({
    config,
    colors,
    fonts,
    styles,
    components,
});
